import {
  IonAlert,
  IonButton,
  IonButtons,
  IonCard,
  IonCardTitle,
  IonContent,
  IonHeader,
  IonIcon,
  IonImg,
  IonMenuButton,
  IonModal,
  IonPage,
  IonSpinner,
  IonTitle,
  IonToolbar,
  isPlatform,
} from "@ionic/react";

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";

import { downloadOutline, navigate, walk } from "ionicons/icons";
import { GoogleMap, MarkerF } from "@react-google-maps/api";

import {
  AddressModel,
  AddressSearch,
  AddressSearchProps,
  EMPTY_ADDRESS,
} from "@/modules/location/components";

import { Location } from "@/modules/shared/shared.types";
import { PWAInstallHelpSheet } from "@/modules/misc/components";
import { logEvent } from "@/modules/firebase";
import { AppGoogleMapsCtx } from "@/modules/shared/ctxs/AppGoogleMapsCtx";
import { useProjectsStore } from "@/core/stores/projects.store";
import { useLocationsHelpers } from "@/modules/locations/hooks";

import "./LocationsNearbyPage.css";

const MAP_CONTAINER_STYLE: React.CSSProperties = {
  width: "100%",
  height: "100%",
};

const MAP_OPTIONS: google.maps.MapOptions = {
  disableDefaultUI: true,
  zoomControl: false,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
};

const getWalkingMinutes = (distanceMeters: number): number =>
  Math.max(1, Math.ceil(distanceMeters / 80));

// Home icon SVG for map markers
const getHomeIcon = (isSelected: boolean): string => {
  const color = isSelected ? "#ea4335" : "#34a853"; // Red for selected, green for unselected
  const size = isSelected ? 40 : 32;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" fill="${color}"/>
      <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" stroke="white" stroke-width="1" fill="none"/>
    </svg>
  `)}`;
};

type Props = {};

export function LocationsNearbyPage({}: Props) {
  const { loaded: mapsLoaded } = useContext(AppGoogleMapsCtx);
  const currentProject = useProjectsStore((state) => state.currentProject);
  const modalInfoRef = useRef<HTMLIonModalElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const cardRefs = useRef<Map<string, HTMLIonCardElement>>(new Map());

  const [isLoading, setIsLoading] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(
    null,
  );

  const [address, setAddress] = useState<AddressModel>(EMPTY_ADDRESS);
  const [targetLocation, setTargetLocation] = useState<{
    lat: number;
    long: number;
  } | null>(null);

  const [showAlertAccessLocation, setShowAlertAccessLocation] = useState(false);
  const [showAlertAccessLocationDenied, setShowAlretAccessLocationDenied] =
    useState(false);
  const isWeb = Capacitor.getPlatform() === "web";
  const isInStandalonePWA =
    typeof window !== "undefined" &&
    (window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone ===
        true);
  const showPWAInstallButton = isWeb && !isInStandalonePWA;

  const [showPWAInstallHelp, setShowPWAInstallHelp] = useState(false);
  const [enlargedImageUrl, setEnlargedImageUrl] = useState<string | null>(null);
  const [enlargedImageTitle, setEnlargedImageTitle] = useState<string>("");
  const [enlargedImageSubtitle, setEnlargedImageSubtitle] =
    useState<string>("");
  const [enlargedLocation, setEnlargedLocation] = useState<Location | null>(null);

  const hasValidLocation =
    address.location.lat !== 0 || address.location.long !== 0;

  const mapCenter = useMemo(
    () => ({
      lat: address.location.lat,
      lng: address.location.long,
    }),
    [address.location.lat, address.location.long],
  );

  const fetchCurrentPosition = useCallback(async () => {
    setIsLoading(true);
    try {
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
      });

      if (!position) {
        return;
      }

      const latitude = position.coords.latitude;
      const longitude = position.coords.longitude;

      console.log(
        `## position, latitude: ${latitude}, longitude: ${longitude}`,
      );

      await getAndSetLocations({ lat: latitude, long: longitude });

      var geocoder = new google.maps.Geocoder();
      var latlng = new google.maps.LatLng(latitude, longitude);
      geocoder.geocode({ location: latlng }, function (results, status) {
        if (status === "OK") {
          if (results?.[0]) {
            var addressText = results[0].formatted_address;
            setAddress({
              text: addressText,
              location: { lat: latitude, long: longitude },
            });
          } else {
            setAddress(EMPTY_ADDRESS);
          }
        } else {
          console.error("Geocoder failed due to: " + status);
        }
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getCurrentLocationShulters = useCallback(async () => {
    setIsLoading(true);
    try {
      const { location: permissionState } =
        await Geolocation.checkPermissions();
      const result = {
        state: permissionState,
      };

      console.log(`## Permission state: ${result.state}`);

      if (result.state === "granted") {
        console.log(`## Permission granted, fetching position`);
        await fetchCurrentPosition();
      } else if (result.state === "prompt") {
        console.log(`## Permission prompt, showing alert`);
        setTimeout(() => {
          console.log(`## Setting showAlertAccessLocation to true`);
          setShowAlertAccessLocation(true);
        }, 100);
      } else {
        console.log(`## Permission denied, showing denied alert`);
        setShowAlretAccessLocationDenied(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const alertLocationButtons = useMemo(
    () => [
      {
        text: "לאשר",
        role: "confirm",
        handler: () => {
          fetchCurrentPosition();
        },
      },
    ],
    [fetchCurrentPosition],
  );

  const alertLocationDeniedButtons = useMemo(
    () => [
      {
        text: "סגור",
        role: "confirm",
        handler: () => {
          setShowAlretAccessLocationDenied(false);
          setTimeout(() => {
            modalInfoRef.current?.present();
          });
        },
      },
    ],
    [],
  );

  useEffect(() => {
    console.log(
      `## showAlertAccessLocation changed to: ${showAlertAccessLocation}`,
    );
  }, [showAlertAccessLocation]);

  useEffect(() => {
    getCurrentLocationShulters();
  }, []);

  const { nearbyLocations, isLoadingNearby } = useLocationsHelpers({
    nearbyLocation: targetLocation,
  });

  const getAndSetLocations = useCallback(
    (location: { lat: number; long: number }) => {
      setTargetLocation(location);
      setSelectedLocationId(null); // Clear selection when list changes
    },
    [],
  );

  const onAddressChange: AddressSearchProps["onChange"] = useCallback(
    (addressModel) => {
      setAddress(addressModel);
      getAndSetLocations(addressModel.location);
    },
    [getAndSetLocations],
  );

  const handleNavigateToLocation = useCallback(
    (location: Location) => {
      const navigationLink = `https://www.google.com/maps/dir/?api=1&origin=${address.location.lat},${address.location.long}&destination=${location.coords.lat},${location.coords.longt}&travelmode=walking`;

      logEvent("navigate_to_location", {
        location_id: location.id,
        location_description: location.description,
        navigation_link: navigationLink,
      });

      window.open(navigationLink, "_blank");
    },
    [address],
  );

  const handleCardClick = useCallback((location: Location) => {
    if (!mapRef.current || !location.id) return;

    const locationPosition = {
      lat: location.coords.lat,
      lng: location.coords.longt,
    };

    // Center and pan to the location
    mapRef.current.panTo(locationPosition);
    mapRef.current.setZoom(17);

    // Set selected location for visual feedback
    setSelectedLocationId(location.id);

    // Scroll card into view
    const cardElement = cardRefs.current.get(location.id);
    if (cardElement) {
      cardElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  const handlePinClick = useCallback((location: Location) => {
    if (!location.id) return;

    // Set selected location
    setSelectedLocationId(location.id);

    // Scroll card into view
    const cardElement = cardRefs.current.get(location.id);
    if (cardElement) {
      cardElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  const openLocationImage = useCallback(
    (event: React.SyntheticEvent, location: Location) => {
      event.stopPropagation();
      const minutes = getWalkingMinutes(location.distance);
      const imageUrl = location.imagesUrls?.[0] ?? "/location-placeholder.png";
      setEnlargedImageUrl(imageUrl);
      setEnlargedImageTitle(`${minutes} דקות הליכה`);
      setEnlargedImageSubtitle(`${location.distance} מטר ממיקומך`);
      setEnlargedLocation(location);
    },
    [],
  );

  const closeLocationImage = useCallback(() => {
    setEnlargedImageUrl(null);
    setEnlargedImageTitle("");
    setEnlargedImageSubtitle("");
    setEnlargedLocation(null);
  }, []);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>

          <IonTitle>{currentProject?.name || "מקלטים קרובים"}</IonTitle>

          <IonButtons slot="end">
            {showPWAInstallButton && (
              <IonButton
                size="small"
                fill="outline"
                onClick={() => setShowPWAInstallHelp(true)}
              >
                <IonIcon slot="start" icon={downloadOutline} />
                הורדה
              </IonButton>
            )}
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent scrollY={false} className="nearby-content">
        <div className="nearby-page-layout">
          {/* ─── Hero: map + search, always visible ──────────────────── */}
          <div className="nearby-hero">
            <div className="nearby-map-area">
              {mapsLoaded && hasValidLocation ? (
                <GoogleMap
                  mapContainerStyle={MAP_CONTAINER_STYLE}
                  center={mapCenter}
                  zoom={15}
                  options={MAP_OPTIONS}
                  onLoad={onMapLoad}
                >
                  <MarkerF position={mapCenter} />

                  {nearbyLocations.map((location) => {
                    const isSelected = selectedLocationId === location.id;
                    return (
                      <MarkerF
                        key={location.id}
                        position={{
                          lat: location.coords.lat,
                          lng: location.coords.longt,
                        }}
                        icon={{
                          url: getHomeIcon(isSelected),
                          scaledSize: new google.maps.Size(
                            isSelected ? 40 : 32,
                            isSelected ? 40 : 32,
                          ),
                          anchor: new google.maps.Point(
                            isSelected ? 20 : 16,
                            isSelected ? 20 : 16,
                          ),
                        }}
                        onClick={() => handlePinClick(location)}
                      />
                    );
                  })}
                </GoogleMap>
              ) : (
                <div className="nearby-map-placeholder">
                  {isLoading && <IonSpinner name="crescent" color="primary" />}
                </div>
              )}
            </div>

            <div className="nearby-search-float">
              <AddressSearch model={address} onChange={onAddressChange} />
            </div>
          </div>

          {/* ─── Sheet: only this part scrolls ────────────────────────── */}
          <div className="nearby-sheet">
            {/* Loading */}
            {(isLoading || isLoadingNearby) && nearbyLocations.length === 0 && (
              <div className="nearby-loading">
                <IonSpinner name="crescent" color="primary" />
              </div>
            )}

            {/* Location Cards */}
            {nearbyLocations.map((location) => {
              const minutes = getWalkingMinutes(location.distance);

              return (
                <IonCard
                  key={location.id}
                  ref={(el) => {
                    if (el && location.id) {
                      cardRefs.current.set(location.id, el);
                    } else if (!el && location.id) {
                      cardRefs.current.delete(location.id);
                    }
                  }}
                  className={
                    selectedLocationId === location.id
                      ? "nearby-card-selected"
                      : ""
                  }
                >
                  <div
                    className="nearby-card-layout"
                    onClick={(e) => {
                      if (
                        (e.target as HTMLElement).closest(
                          ".nearby-card-photo-container",
                        )
                      )
                        return;
                      handleCardClick(location);
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="nearby-card-info">
                      <IonCardTitle>{location.description}</IonCardTitle>

                      <div className="nearby-card-meta">
                        <span className="nearby-card-time-distance">
                          <IonIcon
                            icon={walk}
                            className="nearby-card-meta-icon"
                          />
                          {minutes} דקות ({location.distance} מ&apos;)
                        </span>
                      </div>

                      <div className="nearby-card-button">
                        <IonButton
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNavigateToLocation(location);
                          }}
                        >
                          <IonIcon slot="start" icon={navigate} />
                          נווט
                        </IonButton>
                      </div>
                    </div>

                    <div
                      className="nearby-card-photo-container rounded-lg overflow-hidden"
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openLocationImage(e, location);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openLocationImage(e, location);
                        }
                      }}
                    >
                      {location.imagesUrls?.[0] ? (
                        <IonImg
                          src={location.imagesUrls[0]}
                          alt={location.description}
                          className="nearby-card-photo"
                          style={{ pointerEvents: "none" }}
                        />
                      ) : (
                        <IonImg
                          src="/location-placeholder.png"
                          alt="מקלט"
                          className="nearby-card-photo nearby-card-photo-placeholder"
                          style={{ pointerEvents: "none" }}
                        />
                      )}
                    </div>
                  </div>
                </IonCard>
              );
            })}
          </div>
        </div>
      </IonContent>

      {/* ─── Alerts ─────────────────────────────────────────────────── */}
      <IonAlert
        header="על מנת לקבל מקומך הנוכחי,בבקשה לאפשר גישה למיקום"
        isOpen={showAlertAccessLocation}
        buttons={alertLocationButtons}
        onDidDismiss={({ detail }) => {
          console.log(`## Alert dismissed with role: ${detail.role}`);
          setShowAlertAccessLocation(false);
        }}
      ></IonAlert>

      <IonAlert
        header="על מנת לקבל מקומך הנוכחי, בבקשה לאפשר גישה למיקום עבור האפלקציה בהדרות"
        isOpen={showAlertAccessLocationDenied}
        buttons={alertLocationDeniedButtons}
        onDidDismiss={({ detail }) => {
          console.log(`Dismissed with role: ${detail.role}`);
          setShowAlretAccessLocationDenied(false);
        }}
      ></IonAlert>

      {/* ─── PWA Install Help ───────────────────────────────────────── */}
      {showPWAInstallButton && (
        <PWAInstallHelpSheet
          isOpen={showPWAInstallHelp}
          onDismiss={() => setShowPWAInstallHelp(false)}
        />
      )}

      {/* ─── Image Enlargement Modal ─────────────────────────────────── */}
      <IonModal
        isOpen={enlargedImageUrl !== null}
        onDidDismiss={closeLocationImage}
        className="nearby-image-modal"
        initialBreakpoint={0.9}
        breakpoints={[0.5, 0.75, 0.9, 1]}
      >
        <IonContent className="ion-padding">
          {enlargedImageUrl && (
            <div className="nearby-image-modal-content">
              <div className="nearby-image-modal-heading">
                <h2 className="app-section-title">
                  {enlargedImageTitle || "תמונה"}
                </h2>
                <p className="app-section-subtitle">
                  {enlargedImageSubtitle || "תמונה מוגדלת"}
                </p>
              </div>

              <div className="nearby-image-modal-img-wrapper rounded-lg overflow-hidden">
                <IonImg
                  src={enlargedImageUrl}
                  alt="תמונה מוגדלת"
                  className="nearby-image-modal-img rounded-lg"
                />
              </div>

              <div className="nearby-image-modal-buttons">
                {enlargedLocation && (
                  <IonButton
                    expand="block"
                    size="large"
                    color="primary"
                    onClick={() => handleNavigateToLocation(enlargedLocation)}
                  >
                    <IonIcon slot="start" icon={navigate} />
                    נווט
                  </IonButton>
                )}
                <IonButton
                  expand="block"
                  size="large"
                  color="danger"
                  onClick={closeLocationImage}
                >
                  סגור
                </IonButton>
              </div>
            </div>
          )}
        </IonContent>
      </IonModal>

      {/* ─── Permission Guide Modal ─────────────────────────────────── */}
      <IonModal
        ref={modalInfoRef}
        initialBreakpoint={0.75}
        breakpoints={[0, 0.75, 1]}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>מדריך</IonTitle>
            <IonButtons slot="end">
              <IonButton
                fill="clear"
                onClick={() => modalInfoRef.current?.dismiss()}
              >
                סגור
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent className="ion-padding">
          {isPlatform("android") && (
            <div>
              <div style={{ marginBottom: "var(--app-space-xl)" }}>
                <h2 className="ion-text-center">(1)</h2>
                <hr />
                <IonImg src="https://firebasestorage.googleapis.com/v0/b/mymap-miklats.appspot.com/o/android-web-access-location-1.png?alt=media&token=4c440473-6b49-4581-bb80-5f0f6dba989c" />
              </div>
              <div style={{ marginBottom: "var(--app-space-xl)" }}>
                <h2 className="ion-text-center">(2)</h2>
                <hr />
                <IonImg src="https://firebasestorage.googleapis.com/v0/b/mymap-miklats.appspot.com/o/android-web-access-location-2.png?alt=media&token=d2cd03bf-676a-428a-949f-074f2eac87da" />
              </div>
              <div style={{ marginBottom: "var(--app-space-xl)" }}>
                <h2 className="ion-text-center">(3)</h2>
                <hr />
                <IonImg src="https://firebasestorage.googleapis.com/v0/b/mymap-miklats.appspot.com/o/android-web-access-location-3.png?alt=media&token=e2b58fd2-c3df-4aa5-99ea-8a5cb0bfb25e" />
              </div>
            </div>
          )}

          {isPlatform("ios") && (
            <div>
              <div style={{ marginBottom: "var(--app-space-xl)" }}>
                <h2 className="ion-text-center">(1)</h2>
                <hr />
                <IonImg src="https://firebasestorage.googleapis.com/v0/b/mymap-miklats.appspot.com/o/ios-web-access-location-1.png?alt=media&token=74898430-ee34-4b7e-972a-1830dd87bdd0" />
              </div>
              <div style={{ marginBottom: "var(--app-space-xl)" }}>
                <h2 className="ion-text-center">(2)</h2>
                <hr />
                <IonImg src="https://firebasestorage.googleapis.com/v0/b/mymap-miklats.appspot.com/o/ios-web-access-location-2.png?alt=media&token=103d76a5-40dc-4a1a-ac53-181a3634dc4c" />
              </div>
              <div style={{ marginBottom: "var(--app-space-xl)" }}>
                <h2 className="ion-text-center">(3)</h2>
                <hr />
                <IonImg src="https://firebasestorage.googleapis.com/v0/b/mymap-miklats.appspot.com/o/ios-web-access-location-3.png?alt=media&token=48e078a2-1352-4a75-a4b8-fd5521173260" />
              </div>
            </div>
          )}
        </IonContent>
      </IonModal>
    </IonPage>
  );
}
